import React from 'react';
import { LuCalendarClock } from 'react-icons/lu';
import { cx } from '../../utils/cx.js';
import { dayName, formatDays, formatParisInstant } from '../../utils/weekFormat.js';
import Badge from '../ui/Badge';
import IconChip from '../ui/IconChip';
import { INPUT_CLASS } from '../ui/Field';

const DAYS = [1, 2, 3, 4, 5, 6, 7];
const HOURS = Array.from({ length: 24 }, (_, hour) => hour);
const SELECT_CLASS = cx(INPUT_CLASS, 'pr-7 font-medium');

const previewNote = (period) => {
  if (period.extended) return 'Période allongée pour ne rien perdre depuis le rapport précédent.';
  if (period.overlapDays > 0) {
    return `Ce rapport reprend ${formatDays(period.overlapDays)} jours déjà présents dans S${period.overlapsWeek}. Une seule fois, pour rejoindre le nouveau créneau. Les rapports existants ne changent pas.`;
  }
  return null;
};

/** « Génération du weekly » card: slot pickers and the server preview of the next report. */
const ScheduleCard = ({ day, hour, dirty, preview, disabled, onChange }) => {
  const next = preview.status === 'ready' ? preview.data.proposed : null;
  const note = next && previewNote(next);
  return (
    <section aria-labelledby="schedule-title" className="rounded-xl border border-gray-200 dark:border-gray-800">
      <div className="flex items-start gap-3 border-b border-gray-100 px-4 py-3 dark:border-gray-900">
        <IconChip icon={LuCalendarClock} size="sm" />
        <div className="min-w-0 flex-1">
          <h3 id="schedule-title" className="text-sm font-semibold text-gray-900 dark:text-white">Génération du weekly</h3>
          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">Chaque rapport couvre les 7 jours précédents et reprend exactement où le précédent s'arrête.</p>
        </div>
        {dirty && <Badge tone="amber" size="sm" dot>Non enregistré</Badge>}
      </div>
      <div className="px-4 py-4">
        <div className="flex flex-wrap items-center gap-2 text-sm text-gray-900 dark:text-white">
          <span>Générer le weekly chaque</span>
          <label htmlFor="schedule-day" className="sr-only">Jour de génération</label>
          <select id="schedule-day" value={day} style={{ width: 'auto' }} disabled={disabled} onChange={(e) => onChange('report_day', Number(e.target.value))} className={SELECT_CLASS}>
            {DAYS.map((d) => <option key={d} value={d}>{dayName(d)}</option>)}
          </select>
          <span>à</span>
          <label htmlFor="schedule-hour" className="sr-only">Heure de génération</label>
          <select id="schedule-hour" value={hour} style={{ width: 'auto' }} disabled={disabled} onChange={(e) => onChange('report_hour', Number(e.target.value))} className={cx(SELECT_CLASS, 'tabular-nums')}>
            {HOURS.map((h) => <option key={h} value={h}>{h} h</option>)}
          </select>
        </div>
        <div aria-live="polite" className="mt-4 rounded-lg bg-gray-50 px-3.5 py-3 dark:bg-night-850">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="whitespace-nowrap text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Prochain rapport</span>
            {next && <Badge tone="violet" size="sm">S{next.week}</Badge>}
            {next && <Badge tone="slate" size="sm">{formatDays(next.days)} jours</Badge>}
          </div>
          {preview.status === 'loading' && <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Calcul…</p>}
          {preview.status === 'error' && <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Aperçu indisponible</p>}
          {next && (
            <p className="mt-1 text-sm font-medium tabular-nums text-gray-900 dark:text-white">
              {formatParisInstant(next.start)} <span className="text-gray-400 dark:text-gray-500" aria-hidden>→</span>
              <span className="sr-only">jusqu'au</span> {formatParisInstant(next.end)}
            </p>
          )}
          {note && <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">{note}</p>}
        </div>
      </div>
    </section>
  );
};

export default ScheduleCard;
